// Native-only adapter for the already-registered `QlearnAudioSession` Capacitor
// plugin (the JS name registered by `QlearnBridgeViewController` in the native
// shell, PR135). Dependency-free: the Capacitor runtime is read from the global
// `window.Capacitor` (the same probe `revenueCatService` uses) or injected, so on
// web / non-native / flags-off paths this makes ZERO plugin calls.
//
// Contract order: attach the `routeChange` listener FIRST, THEN `configure()`,
// and only after both succeed may Drive Mode playback start. A plugin setup
// failure safely DISABLES Drive Mode playback (the normal quiz path is
// unaffected) — it never fetches or plays a segment.

const defaultCapacitor = () =>
  (typeof window !== "undefined" ? window.Capacitor : undefined);

/**
 * The native `QlearnAudioSession` plugin, or `null` when this is not a native
 * Capacitor runtime (web / SSR / non-native). `null` means "make no plugin calls".
 */
export function resolveDriveRoutePlugin(capacitor = defaultCapacitor()) {
  if (!capacitor || typeof capacitor.isNativePlatform !== "function") return null;
  if (!capacitor.isNativePlatform()) return null;
  const plugin = capacitor.Plugins && capacitor.Plugins.QlearnAudioSession;
  return plugin || null;
}

/**
 * Attach the `routeChange` listener, THEN `configure()`, in that exact order.
 * Returns a handle: `{ supported, active, remove }`.
 * - `supported=false` → web / non-native: inert, zero plugin calls.
 * - `supported=true, active=true` → listener bound and session configured.
 * - `supported=true, active=false` → setup failed; caller must keep playback disabled.
 * `remove()` detaches the listener exactly once and is safe to call repeatedly;
 * a `routeChange` callback that arrives after teardown (remove, setup failure, or
 * cancellation) is inert. An optional `cancellation` token — `{ cancelled, onCancel }`,
 * cancelled by the caller during a still-pending bind — lets the caller reclaim an
 * already-attached listener exactly once even if `configure()` never settles.
 */
export async function bindDriveRouteChange({ onRouteChange, plugin = resolveDriveRoutePlugin(), cancellation } = {}) {
  if (!plugin) {
    return { supported: false, active: false, remove() {} };
  }
  let removed = false;
  let handle = null;
  // Single removal path: detaches the listener exactly once AND flips the
  // `removed` guard so any late-queued routeChange callback is inert.
  const removeHandle = () => {
    if (removed) return;
    removed = true;
    try {
      if (handle && typeof handle.remove === "function") handle.remove();
    } catch (_) { /* ignore */ }
  };
  const inactive = () => ({ supported: true, active: false, remove: removeHandle });
  try {
    // ORDER (contract §"Binding behavior"): addListener BEFORE configure, so no
    // route change can slip past between a successful configure() and an
    // attached listener.
    handle = await plugin.addListener("routeChange", (event) => {
      if (removed) return; // late callbacks after teardown are inert
      if (typeof onRouteChange === "function") onRouteChange(event);
    });
    // Ownership during pending setup: register the reclaim now that the listener
    // exists, so a cancel() while configure() is unsettled removes the already-
    // attached handle exactly once — even if configure() never settles.
    if (cancellation) {
      if (cancellation.cancelled) { removeHandle(); return inactive(); }
      cancellation.onCancel = removeHandle;
    }
    await plugin.configure();
    // Cancelled during configure(): keep playback disabled and stay removed.
    if (cancellation && cancellation.cancelled) { removeHandle(); return inactive(); }
  } catch (error) {
    // Safe disable: flip the guard so late callbacks are inert, then detach the
    // partial listener exactly once. The caller never starts playback (no segment
    // fetch, no HTMLMediaElement.play()).
    removeHandle();
    return inactive();
  }
  return {
    supported: true,
    active: true,
    remove: removeHandle,
  };
}

export default bindDriveRouteChange;
